"use client";

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useSession } from "next-auth/react";


export interface SavedVacancy {
  id: string | number;
  savedAt: string;
  title?: string;
  company?: string;
  location?: string;
  salary?: string;
  type?: string;
  logo?: string;
  slug?: string;
}

interface SavedVacanciesContextType {
  savedVacancies: SavedVacancy[];
  sentVacancies: SavedVacancy[];
  saveVacancy: (id: string | number, title?: string, extraMeta?: Partial<SavedVacancy>) => void;
  removeVacancy: (id: string | number, title?: string) => void;
  setAllSavedVacancies: (vacancies: SavedVacancy[]) => void;
  isSaved: (id: string | number) => boolean;
  markAsSent: (id: string | number) => void;
  removeSentVacancy: (id: string | number) => void;
  isSent: (id: string | number) => boolean;
  toastMessage: string | null;
  setToastMessage: (msg: string | null) => void;
}

const WP_URL = "https://admin.myjobs.al/wp-json/myjobs/v1/user-data";

const SavedVacanciesContext = createContext<SavedVacanciesContextType | undefined>(undefined);

function normalizeId(id: string | number): string {
  let strId = String(id).trim();
  if (strId.length > 10 && /^[A-Za-z0-9+/=]+$/.test(strId)) {
    try {
      const decoded =
        typeof window !== "undefined" && window.atob
          ? window.atob(strId)
          : Buffer.from(strId, "base64").toString();
      if (decoded.includes(":")) {
        const parts = decoded.split(":");
        if (parts[1] && !isNaN(Number(parts[1]))) return String(parts[1]);
      }
    } catch (e) {}
  }
  return strId;
}

function parseWpJson(val: unknown): SavedVacancy[] {
  let v: unknown = val;
  while (typeof v === "string" && v.trim() !== "") {
    try {
      const p = JSON.parse(v);
      if (p === v) break;
      v = p;
    } catch (e) {
      break;
    }
  }
  return Array.isArray(v) ? (v as SavedVacancy[]) : [];
}

function getUserKeys(): {
  email: string | null;
  wpId: string | null;
  savedKey: string;
  sentKey: string;
} {
  if (typeof window === "undefined")
    return {
      email: null,
      wpId: null,
      savedKey: "savedVacancies_guest",
      sentKey: "sentVacancies_guest",
    };
  const emailMatch = document.cookie.match(/(^| )userEmail=([^;]+)/);
  const email =
    localStorage.getItem("userEmail") ||
    (emailMatch ? decodeURIComponent(emailMatch[2]) : null);
  const idMatch = document.cookie.match(/(^| )wpUserId=([^;]+)/);
  const wpId =
    localStorage.getItem("wpUserId") || (idMatch ? idMatch[2] : null);
  const safeEmail = email
    ? email.toLowerCase().trim().replace(/[^a-z0-9]/g, "_")
    : null;
  const savedKey = safeEmail
    ? `savedVacancies_${safeEmail}`
    : wpId
    ? `savedVacancies_id_${wpId}`
    : "savedVacancies_guest";
  const sentKey = safeEmail
    ? `sentVacancies_${safeEmail}`
    : wpId
    ? `sentVacancies_id_${wpId}`
    : "sentVacancies_guest";
  return { email, wpId, savedKey, sentKey };
}

async function writeToWP(
  saved: SavedVacancy[],
  sent: SavedVacancy[],
  email: string | null,
  wpId: string | null
) {
  if (!email && !wpId) return;
  try {
    await fetch(WP_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: wpId,
        user_email: email,
        saved_vacancies: saved,
        sent_vacancies: sent,
      }),
    });
  } catch (e) {
    console.error("writeToWP failed:", e);
  }
}

async function readFromWP(
  email: string | null,
  wpId: string | null
): Promise<{ saved: SavedVacancy[]; sent: SavedVacancy[] } | null> {
  if (!email && !wpId) return null;
  const parts: string[] = [];
  if (wpId) parts.push(`user_id=${encodeURIComponent(wpId)}`);
  if (email) parts.push(`user_email=${encodeURIComponent(email)}`);
  try {
    const res = await fetch(`${WP_URL}?${parts.join("&")}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json();
    return {
      saved: parseWpJson(data.saved_vacancies),
      sent: parseWpJson(data.sent_vacancies),
    };
  } catch (e) {
    console.error("readFromWP failed:", e);
    return null;
  }
}

export function SavedVacanciesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [savedVacancies, setSavedVacancies] = useState<SavedVacancy[]>([]);
  const [sentVacancies, setSentVacancies] = useState<SavedVacancy[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const { data: session, status } = useSession();


  // Refs so async/setState callbacks always have latest state without stale closure
  const sentRef = useRef<SavedVacancy[]>([]);
  const savedRef = useRef<SavedVacancy[]>([]);
  const fetchedRef = useRef(false); // prevent double-fetch
  
  useEffect(() => {
    sentRef.current = sentVacancies;
    savedRef.current = savedVacancies;
  }, [sentVacancies, savedVacancies]);

  // Core fetch & display logic (called from either localStorage or session effect)
  const loadUserData = useCallback((email: string | null, wpId: string | null) => {
    if (!email && !wpId) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Compute storage keys
    const safeEmail = email ? email.toLowerCase().trim().replace(/[^a-z0-9]/g, '_') : null;
    const savedKey = safeEmail ? `savedVacancies_${safeEmail}` : `savedVacancies_id_${wpId}`;
    const sentKey  = safeEmail ? `sentVacancies_${safeEmail}`  : `sentVacancies_id_${wpId}`;

    // Persist credentials so next refresh works without session round-trip
    if (typeof window !== 'undefined' && sessionStorage.getItem('logging_out') === 'true') return;
    if (email && !localStorage.getItem('userEmail')) localStorage.setItem('userEmail', email);
    if (email) localStorage.setItem('isLoggedIn', 'true');
    if (wpId && !localStorage.getItem('wpUserId')) localStorage.setItem('wpUserId', String(wpId));

    // 1. Instantly show cached data
    const cached = localStorage.getItem(savedKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) setSavedVacancies(parsed);
      } catch (e) {}
    }
    const cachedSent = localStorage.getItem(sentKey);
    if (cachedSent) {
      try {
        const parsed = JSON.parse(cachedSent);
        if (Array.isArray(parsed)) setSentVacancies(parsed);
      } catch (e) {}
    }

    // 2. Authoritative fetch from WordPress
    readFromWP(email, wpId).then((result) => {
      if (!result) {
        console.log('[SavedVacancies] WP fetch null — keeping cache');
        return;
      }
      console.log('[SavedVacancies] WP returned', result.saved.length, 'saved items');

      // If WP empty but localStorage has data, restore WP from cache
      const localRaw = localStorage.getItem(savedKey);
      let localItems: SavedVacancy[] = [];
      try { localItems = localRaw ? JSON.parse(localRaw) : []; } catch (e) {}

      if (result.saved.length === 0 && localItems.length > 0) {
        console.log('[SavedVacancies] WP wiped — restoring', localItems.length, 'items from cache');
        writeToWP(localItems, sentRef.current, email, wpId);
        setSavedVacancies(localItems);
        return;
      }

      setSavedVacancies(result.saved);
      setSentVacancies(result.sent);
      try {
        localStorage.setItem(savedKey, JSON.stringify(result.saved));
        localStorage.setItem(sentKey, JSON.stringify(result.sent));
      } catch (e) {}
    });

    // 3. Cross-tab sync
    const handleStorage = (e: StorageEvent) => {
      if (e.key === savedKey && e.newValue) {
        try { setSavedVacancies(JSON.parse(e.newValue)); } catch (e) {}
      }
      if (e.key === sentKey && e.newValue) {
        try { setSentVacancies(JSON.parse(e.newValue)); } catch (e) {}
      }
    };
    window.addEventListener('storage', handleStorage);
  }, []);

  // Effect 1: try localStorage credentials on mount (fast path — works after custom login)
  useEffect(() => {
    setIsMounted(true);
    const { email, wpId } = getUserKeys();
    console.log('[SavedVacancies] Mount — email:', email, '| wpId:', wpId);
    if (email || wpId) {
      loadUserData(email, wpId);
    }
  }, [loadUserData]);

  // Effect 2: NextAuth session path (Google/Facebook OAuth login)
  useEffect(() => {
    if (status !== 'authenticated' || !session) return;
    const sessionEmail = session.user?.email ?? null;
    const sessionWpId = String((session as any).wpUserId ?? '');
    console.log('[SavedVacancies] Session — email:', sessionEmail, '| wpId:', sessionWpId);
    loadUserData(sessionEmail, sessionWpId || null);
  }, [status, session, loadUserData]);


  // Toast auto-dismiss
  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const saveVacancy = useCallback(
    (
      id: string | number,
      title?: string,
      extraMeta?: Partial<SavedVacancy>
    ) => {
      const normId = normalizeId(id);
      const { email, wpId, savedKey } = getUserKeys();

      // Use setState callback so we always work with the LATEST state
      setSavedVacancies((prev) => {
        if (prev.some((v) => normalizeId(v.id) === normId)) return prev;
        const newVacancy: SavedVacancy = {
          id: normId,
          title: title || extraMeta?.title,
          company: extraMeta?.company,
          location: extraMeta?.location,
          salary: extraMeta?.salary,
          type: extraMeta?.type,
          logo: extraMeta?.logo,
          slug: extraMeta?.slug,
          savedAt: new Date().toISOString(),
        };
        const next = [...prev, newVacancy];
        try {
          localStorage.setItem(savedKey, JSON.stringify(next));
        } catch (e) {}
        // sentRef.current is always the latest sentVacancies
        writeToWP(next, sentRef.current, email, wpId);
        return next;
      });

      const isSq =
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/sq");
      setToastMessage(
        isSq
          ? title
            ? `"${title}" u ruajt me sukses!`
            : "Njoftimi u ruajt me sukses!"
          : title
          ? `"${title}" was saved successfully!`
          : "Vacancy saved successfully!"
      );
    },
    []
  );

  const removeVacancy = useCallback(
    (id: string | number, title?: string) => {
      const normId = normalizeId(id);
      const { email, wpId, savedKey } = getUserKeys();

      setSavedVacancies((prev) => {
        const next = prev.filter((v) => normalizeId(v.id) !== normId);
        try {
          localStorage.setItem(savedKey, JSON.stringify(next));
        } catch (e) {}
        writeToWP(next, sentRef.current, email, wpId);
        return next;
      });

      const isSq =
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/sq");
      setToastMessage(
        isSq
          ? title
            ? `"${title}" u hoq nga njoftimet e ruajtura.`
            : "Njoftimi u hoq."
          : title
          ? `"${title}" was removed from saved.`
          : "Vacancy removed."
      );
    },
    []
  );

  const setAllSavedVacancies = useCallback((newSaved: SavedVacancy[]) => {
    const { email, wpId, savedKey } = getUserKeys();
    setSavedVacancies(newSaved);
    try {
      localStorage.setItem(savedKey, JSON.stringify(newSaved));
    } catch (e) {}
    writeToWP(newSaved, sentRef.current, email, wpId);
  }, []);

  const markAsSent = useCallback((id: string | number) => {
    const normId = normalizeId(id);
    const { email, wpId, savedKey, sentKey } = getUserKeys();

    setSavedVacancies((prev) => {
      const newSaved = prev.filter((v) => normalizeId(v.id) !== normId);
      setSentVacancies((prevSent) => {
        const newSent = prevSent.some((v) => normalizeId(v.id) === normId)
          ? prevSent
          : [...prevSent, { id: normId, savedAt: new Date().toISOString() }];
        try {
          localStorage.setItem(sentKey, JSON.stringify(newSent));
        } catch (e) {}
        writeToWP(newSaved, newSent, email, wpId);
        return newSent;
      });
      try {
        localStorage.setItem(savedKey, JSON.stringify(newSaved));
      } catch (e) {}
      return newSaved;
    });
  }, []);

  const removeSentVacancy = useCallback((id: string | number) => {
    const normId = normalizeId(id);
    const { email, wpId, sentKey } = getUserKeys();

    setSentVacancies((prevSent) => {
      const newSent = prevSent.filter((v) => normalizeId(v.id) !== normId);
      try {
        localStorage.setItem(sentKey, JSON.stringify(newSent));
      } catch (e) {}
      writeToWP(savedRef.current, newSent, email, wpId);
      return newSent;
    });
  }, []);

  const isSaved = useCallback(
    (id: string | number) => {
      return savedVacancies.some((v) => normalizeId(v.id) === normalizeId(id));
    },
    [savedVacancies]
  );

  const isSent = useCallback(
    (id: string | number) => {
      return sentVacancies.some((v) => normalizeId(v.id) === normalizeId(id));
    },
    [sentVacancies]
  );

  return (
    <SavedVacanciesContext.Provider
      value={{
        savedVacancies,
        sentVacancies,
        saveVacancy,
        removeVacancy,
        setAllSavedVacancies,
        isSaved,
        markAsSent,
        removeSentVacancy,
        isSent,
        toastMessage,
        setToastMessage,
      }}
    >
      {children}
      {isMounted && toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            backgroundColor: "#10b981",
            color: "#ffffff",
            padding: "0.85rem 1.25rem",
            borderRadius: "0.75rem",
            boxShadow: "0 10px 25px -5px rgba(0,0,0,0.18)",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            zIndex: 99999,
            fontSize: "0.925rem",
            fontWeight: 500,
            animation: "fadeToastIn 0.3s ease-out",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>{toastMessage}</span>
          <button
            onClick={() => setToastMessage(null)}
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
              marginLeft: "0.5rem",
              opacity: 0.8,
              fontSize: "1rem",
            }}
          >
            x
          </button>
        </div>
      )}
    </SavedVacanciesContext.Provider>
  );
}

export function useSavedVacancies() {
  const context = useContext(SavedVacanciesContext);
  if (context === undefined)
    throw new Error(
      "useSavedVacancies must be used within a SavedVacanciesProvider"
    );
  return context;
}
